import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { LocalStorage } from '@isophi/core-legacy';
import { EpErrorService, IconService, ToastService } from '@isophi/core-ui';
import {
  AbsenceEnum,
  AbsenceReasonEnum,
  AttendanceRange,
  AttendanceService,
  AttendanceStatusEnum,
  ChildAttendanceOverview,
  ClassCreateOrUpdateAttendance,
  CreateOrUpdateAttendance,
  OverviewAttendance,
} from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { formatDate } from 'date-fns';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs';

import { FORMAT_DATE } from '../../../../core/constants/constant';
import { HelperService } from '../../../../core/services/helper.service';
import { LocalStorageConstService } from '../../../../shared/services/local-storage-const.service';
import { EvidenceService } from '../../services/evidence.service';
import { timeList } from './data';

enum AttendanceType {
  PRESENT_TYPE = 'present_type',
  ABSENT_TYPE = 'absent_type',
  MISSING_TYPE = 'missing_type',
}

const DEFAULT_ARRIVAL = '08:00';
const DEFAULT_DEPARTURE = '16:00';

@Component({
  selector: 'app-edit-absence-modal',
  templateUrl: './edit-absence-modal.component.html',
  styleUrls: ['./edit-absence-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditAbsenceModalComponent implements OnInit {
  private readonly epErrorService = inject(EpErrorService);
  private readonly helperService = inject(HelperService);

  dateInputType: 'hours' | 'days' = 'hours';

  childAttendanceOverview: ChildAttendanceOverview;

  dateFrom: NgbDateStruct | null = this.helperService.parseDate(formatDate(new Date(), FORMAT_DATE, {}));

  dateTo: NgbDateStruct | null = this.dateFrom;

  children: ChildAttendanceOverview[];

  absenceEnum = AbsenceEnum;

  absence: AbsenceEnum;

  attendanceTypeEnum = AttendanceType;

  attendanceType = AttendanceType.PRESENT_TYPE;

  absenceReasonEnum = AbsenceReasonEnum;

  absenceReason: AbsenceReasonEnum;

  timeList = timeList;

  arrival = DEFAULT_ARRIVAL;

  departure = DEFAULT_DEPARTURE;

  groupUuid: string;

  title = 'evidence/editAttendance';

  bulkUpdate = false;

  bulkDates: AttendanceRange[] | null = null;

  showChildrenNames = false;

  protected excludeFromExport = false;

  selectedAttendace: OverviewAttendance | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    public icons: IconService,
    private toastr: ToastService,
    private translocoService: TranslocoService,
    private attendanceService: AttendanceService,
    private spinner: NgxSpinnerService,
    private lSConstService: LocalStorageConstService,
    private evidenceService: EvidenceService
  ) {
    const storage = new LocalStorage();
    if (storage.hasItem(this.lSConstService.childrenFilter)) {
      const storageData = storage.getItem(this.lSConstService.childrenFilter);
      if (storageData && storageData !== 'undefined') {
        const storageDataValues = JSON.parse(storageData);
        this.groupUuid = storageDataValues.group;
      }
    }
  }

  ngOnInit(): void {
    if (this.childAttendanceOverview) {
      this.arrival = this.childAttendanceOverview.attendance[0].arrivalTime || this.arrival;
      this.departure = this.childAttendanceOverview.attendance[0].departureTime || this.departure;
    } else if (this.children?.length === 1 && !this.selectedAttendace) {
      this.arrival = this.children[0]?.attendance[0]?.arrivalTime || this.arrival;
      this.departure = this.children[0]?.attendance[0]?.departureTime || this.departure;
    } else if (this.selectedAttendace) {
      this.arrival = this.selectedAttendace.arrivalTime || this.arrival;
      this.departure = this.selectedAttendace.departureTime || this.departure;
    }
  }

  selectArrivalTime(time): void {
    this.arrival = time;
  }

  selectDepartureTime(time): void {
    this.departure = time;
  }

  onChangeAttendanceType(): void {
    if (this.attendanceType === this.attendanceTypeEnum.PRESENT_TYPE) {
      this.absence = null;
      this.absenceReason = undefined;

      this.arrival = DEFAULT_ARRIVAL;
      this.departure = DEFAULT_DEPARTURE;
      this.excludeFromExport = false;
    } else if (this.attendanceType === this.attendanceTypeEnum.MISSING_TYPE) {
      this.absence = null;
      this.absenceReason = undefined;

      this.arrival = DEFAULT_ARRIVAL;
      this.departure = DEFAULT_DEPARTURE;

      if (this.childAttendanceOverview) {
        this.excludeFromExport = this.childAttendanceOverview.attendance[0].excludeFromExport || this.excludeFromExport;
      } else if (this.children?.length === 1 && !this.selectedAttendace) {
        this.excludeFromExport = this.children[0]?.attendance[0]?.excludeFromExport || this.excludeFromExport;
      } else if (this.selectedAttendace) {
        this.excludeFromExport = this.selectedAttendace.excludeFromExport || this.excludeFromExport;
      }
    } else {
      this.arrival = null;
      this.departure = null;
      this.absenceReason = AbsenceReasonEnum.Other;
      this.absence = AbsenceEnum.Absent;
      this.excludeFromExport = false;
    }
  }

  submitButton(): void {
    this.spinner.show();
    const status =
      this.attendanceType === this.attendanceTypeEnum.PRESENT_TYPE ? AttendanceStatusEnum.Present : AttendanceStatusEnum.Absent;
    const childUuids = this.childAttendanceOverview
      ? [this.childAttendanceOverview.childUuid]
      : this.children.map((child) => child.childUuid);

    const handleSuccess = () => {
      let successText = '';
      switch (this.attendanceType) {
        case AttendanceType.ABSENT_TYPE:
          successText = 'evidence/absentSuccessToast';
          break;
        case AttendanceType.PRESENT_TYPE:
          successText = 'evidence/presentSuccess';
          break;
        case AttendanceType.MISSING_TYPE:
          successText = 'evidence/missingSuccess';
          break;
        default:
          successText = 'evidence/presentSuccess';
      }
      this.toastr.success(this.translocoService.translate(successText));
      this.evidenceService.reloadChildren();
    };

    const handleError = (e) => {
      if (e.error?.arrival_time) {
        e.error.arrival_time.forEach((time) => {
          this.toastr.error(time);
        });
      } else if (e.error?.departure_time) {
        e.error.departure_time.forEach((time) => {
          this.toastr.error(time);
        });
      } else if (e.error?.attendance) {
        this.toastr.error(e.error.attendance.join('\n'));
      } else {
        this.epErrorService.handleEpErrorResponse(e, { defaultMessage: this.translocoService.translate('evidence/actionError') });
      }
    };

    // Bulk attendance updates
    if (this.bulkUpdate && this.bulkDates?.length > 0) {
      const data: CreateOrUpdateAttendance[] = this.bulkDates.map((range) => {
        const updateData = {
          childUuids: childUuids,
          status: status,
          ...(this.absence && { absence: this.absence }),
          ...(this.arrival && { arrivalTime: this.arrival }),
          ...(this.departure && { departureTime: this.departure }),
          dateFrom: range.startDate,
          dateTo: range.endDate,
          absenceReason: this.absenceReason,
          excludeFromExport: this.excludeFromExport,
        };
        if (this.attendanceType === AttendanceType.MISSING_TYPE) {
          updateData.status = 'no_evidence';
          updateData.arrivalTime = null;
          updateData.departureTime = null;
        }
        return updateData;
      });
      this.attendanceService
        .attendanceSetBulkClassAttendanceCreate({
          classUuid: this.groupUuid,
          updates: data,
        })
        .pipe(
          finalize(() => {
            this.spinner.hide();
            this.activeModal.close();
          })
        )
        .subscribe({
          next: () => {
            handleSuccess();
          },
          error: (error) => {
            handleError(error);
          },
        });

      return;
    }

    const data: ClassCreateOrUpdateAttendance = {
      classUuid: this.groupUuid,
      childUuids: childUuids,
      status: status,
      absenceReason: this.absenceReason,
      ...(this.absence && { absence: this.absence }),
      ...(this.arrival && { arrivalTime: this.arrival }),
      ...(this.departure && { departureTime: this.departure }),
      dateFrom: this.helperService.formateDate(this.dateFrom),
      dateTo: this.helperService.formateDate(this.dateTo),
      excludeFromExport: this.excludeFromExport,
    };

    if (this.attendanceType === AttendanceType.MISSING_TYPE) {
      data.status = 'no_evidence';
      data.arrivalTime = null;
      data.departureTime = null;
    }

    this.attendanceService
      .attendanceSetCurrentClassAttendanceCreate(data)
      .pipe(
        finalize(() => {
          this.spinner.hide();
          this.activeModal.close();
        })
      )
      .subscribe({
        next: () => {
          handleSuccess();
        },
        error: (e) => {
          handleError(e);
        },
      });
  }
}
